import { IGlobalStore } from 'src/types/store'
import {
  TStateCommunications,
  createStateSagaCommunications,
} from 'src/helpers/redux'

import { IModuleConfig } from './index'
import { Actions } from './actions'
import * as smartListTypes from 'src/services/storage/project/scheme/smart-list/types'
import * as schemaService from 'src/services/storage/project/scheme/types'
import type { ISchemeRelations } from 'src/services/storage/project/scheme/relations/types'
import { getSession } from 'src/services/session'
import { IObjectBoardConfig } from 'src/helpers/storageObjects'
import {
  IPostImportDataOptions,
  EImportDataStrategy,
} from 'src/services/storage/project/scheme/import/types'

export * from 'src/services/storage/project/scheme/types'

export type FieldValue = string | number | null | undefined

export interface IListItem extends schemaService.IGetObjectsData {
  commentsCount?: number
}

export interface IUpdateObjectItem {
  data: IListItem
}

/**
 * `main` is the sticky first column, `parent` the synthetic parent object
 * column, `own` a field of the scheme, `parentField` a field of the parent
 * scheme and `relatedField` a field of the scheme behind one of the own
 * relationship fields.
 */
export type TColumnKind =
  | 'main'
  | 'parent'
  | 'own'
  | 'parentField'
  | 'relatedField'

/** Where a related column (`parentField` / `relatedField`) takes its values from */
export interface IColumnRelation {
  kind: 'parent' | 'relationship'
  /** Relationship field of the current scheme the column goes through (`relationship` only) */
  fieldIdentifier?: string
  fieldTitle?: string
  /** Behind a multivalue relationship field the cell shows one value per related object and cannot be sorted */
  isMultivalue: boolean
  schemeIdentifier: string
  schemeTitle: string
}

export interface IColumn {
  identifier: string
  objectValueKey: string
  slug: string
  enable: boolean
  width?: number
  priority: {
    index: number
    static: boolean
  }
  /** The field schema; for related columns the schema of the related field */
  schema?: schemaService.ISchema
  /** Missing on columns restored from an old localStorage cache: always read it through `getColumnKind()` */
  kind?: TColumnKind
  /**
   * `sort[...]` / `related_fields[]` key: the identifier of an own field,
   * `parent_object.primary` for the parent column, a dotted path
   * (`parent_object.<field>`, `<relationship field>.<field>`) for related columns
   */
  path?: string
  relation?: IColumnRelation
}

/**
 * Saved user-options entry of a related column that cannot be resolved
 * right now (related scheme not loaded, deleted field, lost permission).
 * It is written back untouched on the next save so the setting survives.
 */
export interface IUnresolvedColumn {
  identifier: string
  enable: boolean
  width?: number
  index: number
}

export interface IStateData {
  smartList: {
    itemDefault?: smartListTypes.IGetSmartListItemData
    itemDefaultUserOptions?: smartListTypes.IPatchSmartListDefaultUserOptionsData
    item?: smartListTypes.IGetSmartListItemData
    permissions: smartListTypes.IGetSmartListPermissionsData

    list: {
      items: smartListTypes.IGetSmartListData
      nextCursor?: string | null
    }
  }

  objects: {
    item?: IListItem
    deleteValidation: schemaService.IDeleteValidationObjectByIdData[] | null
    list: {
      parentObjectUniqId?: string
      items: IListItem[]
      previousCursor: string | null
      nextCursor: string | null
      total?: number
    }
  }

  fieldsGroups: {
    list: {
      items: schemaService.IFieldsGroupData[]
      previousCursor: string | null
      nextCursor: string | null
    }
  }
  table: {
    columns: IColumn[]
    unresolvedColumns: IUnresolvedColumn[]
    filters: { [key: string]: unknown }
  }
  board: {
    config: IObjectBoardConfig
    filters: { [key: string]: unknown }
  }
  /**
   * GET /scheme/{scheme}/relations: `loaded` once it has answered in this
   * session, `item` is `null` when the endpoint is unavailable (old backend)
   * or failed, `undefined` when it never answered.
   */
  relations: {
    item?: ISchemeRelations | null
    loaded: boolean
  }
  /** Fields of the related schemes the related columns are built from, by scheme identifier */
  relatedFields: {
    bySchemeName: Record<string, schemaService.ISchema[]>
  }
  import?: {
    strategy?: `${EImportDataStrategy}`
    options?: IPostImportDataOptions
    mapping?: {
      columnName: string
      fieldIdentifier: string
      primary: boolean
    }[]
  }
}

export interface IState {
  data: IStateData
  communication: TStateCommunications<Actions>
}
const getStorageState = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) || '{}')
  } catch (error) {
    return {}
  }
}
export const getInitialData = (config: IModuleConfig): IStateData => {
  const stateData = getStorageState(config.prefix)

  const session = getSession()
  const isCurrent = (item: IModuleConfig) =>
    item._prefix === config._prefix && item.schemeName === config.schemeName
  const storageData = (session.storage || []).find(isCurrent)

  const data: IStateData = {
    smartList: {
      item: undefined,
      permissions: [],
      itemDefault: storageData?.smartListItemDefault || undefined,
      list: {
        items: storageData?.smartListsItems || [],
        nextCursor: undefined,
      },
    },

    objects: {
      item: undefined,
      deleteValidation: null,
      list: {
        items: storageData?.objectItems || [],
        previousCursor: null,
        nextCursor: null,
      },
    },

    fieldsGroups: {
      list: {
        items: storageData?.fieldsGroupsItems || [],
        previousCursor: null,
        nextCursor: null,
      },
    },

    table: storageData?.table || {
      columns: [],
      unresolvedColumns: [],
      filters: {},
    },
    board: storageData?.board || {
      config: [],
      filters: {},
    },
    relations: {
      item: undefined,
      loaded: false,
    },
    relatedFields: {
      bySchemeName: {},
    },

    ...stateData,
    import: {},
  }

  return {
    ...data,
    table: {
      ...data.table,
      unresolvedColumns: data.table?.unresolvedColumns || [],
    },
    // The cached relations keep the related columns in place until GET /relations
    // answers again; `loaded` is never restored so stale keys are sanitized only
    // against a fresh answer and the request is made on every mount
    relations: {
      item: data.relations?.item,
      loaded: false,
    },
    relatedFields: {
      bySchemeName: data.relatedFields?.bySchemeName || {},
    },
  }
}

export const getInitialState = (
  config: IModuleConfig,
  actions: Actions
): IState => ({
  data: getInitialData(config),
  communication: createStateSagaCommunications(actions),
})

export type TGlobalStateWithService = IGlobalStore & {
  [key: string]: IState
}
